import './styles.css';

import React, { useCallback, useMemo } from 'react';
import { Badge as DSBadge, Typography, Button, Link } from '@vtblife/uikit';

import classname from '@search/classname/src';
import { formatNumber } from '@search/helpers/src/formatNumber';
import {
    OfferSnippetModel,
    priceCompat,
    useOfferCardLink,
    useOfferRouteListCompat,
    useOfferVisited,
    OfferSimilarNb
} from '@search/vtbeco-frontend-core/domain/offer/search/page/SearchOffersQuery';
import { useClassifiedOfferDataLayerPush } from '@search/vtbeco-frontend-core/domain/offer/analytics/ClassifiedOfferGtmContext';
import { ImageLazySeo } from '@search/vtbeco-frontend-core/domain/image/lazy/ImageLazySeo';
import { OfferRouteCard } from '@search/vtbeco-frontend-core/view/common/components/OfferRouteCard';
import { ArrowButtonPair } from '@search/vtbeco-frontend-core/view/common/components/CardScroller/ArrowButton/ArrowButtonPair';
import { useArrowButton } from '@search/vtbeco-frontend-core/view/common/components/CardScroller/ArrowButton/useArrowButton';
import { CardScroller } from '@search/vtbeco-frontend-core/view/common/components/CardScroller/CardScroller';
import { ButtonKitFixer } from './ButtonKitFixer';

const cn = classname.bind(null, 'SimilarNewBuildingOffersSection');

const OfferMiniCard = ({
    model,
    isOffer,
    mixedNewBuilding
} : {
    model: OfferSimilarNb;
    isOffer?: boolean;
    mixedNewBuilding?: boolean;
}) => {
    const { offerUrl } = useOfferCardLink(model);
    const offerRoutes = useOfferRouteListCompat(model);
    const price = priceCompat(model?.dealType);
    const ro = model?.realtyObject?.__typename === 'ProfOfferFlat' ? model?.realtyObject : undefined;
    const imageIsLayout = useMemo(() => {
        return model.gallery?.mainImage.attributesType === 'LAYOUT';
    }, [ model.gallery?.mainImage.attributesType ]);
    const image = useMemo(() => {
        return imageIsLayout ? {
            src: model.gallery?.mainImage?.small,
            width: 520,
            height: 420
        } : {
            src: model.gallery?.mainImage?.preview1,
            width: 250,
            height: 180
        };
    }, [ imageIsLayout, model.gallery?.mainImage ]);
    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const { isVisited, enableIsVisited } = useOfferVisited(model);
    const sendAnalytics = React.useCallback(() => {
        enableIsVisited();
        dataLayerPush({
            event: 'up_offer_click',
            section: 'nb_gallery',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            page_type: isOffer ? 'card' : 'serp'
        });
    }, [ dataLayerPush, enableIsVisited, isOffer ]);

    return (
        <div className={cn('card')}>
            <div className={cn('img-wrap')}>
                <ImageLazySeo
                    originPath={model?.gallery?.mainImage?.originPath ?? ''}
                    src={image.src}
                    alt={model.text?.titleSnippet}
                    title={model.text?.titleSnippet}
                    imgClassName={cn('image', {
                        visited: isVisited ?? undefined,
                        isLayout: imageIsLayout
                    })}
                    srcHeight={image.height} // min-side
                    srcWidth={image.width}
                />
                <div className={cn('badges')}>
                    {isVisited && (
                        <DSBadge color='black' variant='light'>Просмотрено</DSBadge>
                    )}
                    {mixedNewBuilding && ro?.building?.newBuildingStatus?.isKeysComingSoonBadge && (
                        <DSBadge color='blue'>Скоро ключи</DSBadge>
                    )}
                    {mixedNewBuilding && ro?.building?.newBuildingStatus?.isDoneBadge && (
                        <DSBadge color='blue'>Сдан</DSBadge>
                    )}
                </div>
            </div>
            <div className={cn('description')}>
                {mixedNewBuilding && ro?.building?.newBuildingBasicInfo?.title ? (
                    <Typography variant='secondary-alone' color='black-400' className={cn('newBuildingTitle')}>
                        {ro?.building?.newBuildingBasicInfo?.title}
                    </Typography>
                ) : null}
                <div className={cn('cardTitle', { visited: isVisited ?? undefined })}>
                    <Typography variant='primary-alone' bold color='link'>
                        <a href={offerUrl ?? ''}>
                            {model.text?.titleSnippet}
                        </a>
                    </Typography>
                </div>
                <Typography variant='primary-alone' bold>
                    {`${formatNumber(price?.rubPrice ?? price?.value)} ₽`}
                </Typography>
                {mixedNewBuilding && ro?.building?.newBuildingStatus?.formattedInProgress ? (
                    <Typography variant='secondary-alone'>
                        {ro.building.newBuildingStatus.formattedInProgress}
                    </Typography>
                ) : null}
                <div className={cn('address')}>
                    {offerRoutes.length ? offerRoutes.slice(0, 1)
                        .map(offerRoute =>
                            <OfferRouteCard
                                noClickable
                                variant='secondary-alone'
                                key={offerRoute.metro.id}
                                offerRoute={offerRoute}
                                withoutTime
                            />
                        ) : (<Typography variant='secondary-alone'>{model.location?.formattedAddressLong}</Typography>
                    )}
                </div>
            </div>
            <a
                className={cn('overlay-link')}
                href={offerUrl ?? ''}
                onClick={sendAnalytics}
            />
        </div>
    );
};

export function SimilarOffers({
    isMobile,
    isOffer = false,
    mixedNewBuilding = false,
    onAllOffersClick,
    offers,
    title,
    titleSecondary,
    dataTestPrefix = 'offer',
    newBuildingUrl
}: {
    dataTestPrefix?: string;
    title?: string;
    titleSecondary?: string;
    isMobile?: boolean;
    isOffer?: boolean;
    mixedNewBuilding?: boolean;
    onAllOffersClick?: () => void;
    offers: readonly OfferSnippetModel[];
    newBuildingUrl?: string;
}) {
    const {
        onArrowClick,
        onContainerScroll,
        isLeftEdge,
        isRightEdge,
        isScrollNeeded,
        containerRef
    } = useArrowButton();

    const showAllHandler = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
        e.preventDefault();
        if (onAllOffersClick) {
            onAllOffersClick();
        }
    }, [ onAllOffersClick ]);

    return (
        <section className={cn(null, { isOffer })}>
            <div className={cn('header')}>
                <div className={cn('title')}>
                    {title && (<Typography variant='h3'>{title}</Typography>)}
                    {titleSecondary && (<Typography variant='h3' color='secondary'>{titleSecondary}</Typography>)}
                </div>
                {! isMobile && onAllOffersClick && offers?.length > 4 && (
                    <ButtonKitFixer>
                        <Link
                            onClick={showAllHandler}
                            href={newBuildingUrl ?? ''}
                        >Показать все объявления</Link>
                    </ButtonKitFixer>
                )}
                {! isMobile && ! onAllOffersClick && isScrollNeeded && (
                    <div className={cn('arrows')}>
                        <ArrowButtonPair
                            size='s'
                            dataTestPrefix={`${dataTestPrefix}-similar`}
                            variant='alt'
                            onArrowClick={onArrowClick}
                            isLeftEdge={isLeftEdge}
                            isRightEdge={isRightEdge}
                        />
                    </div>
                )}
            </div>
            <CardScroller
                autoWidth
                onContainerScroll={onContainerScroll}
                containerRef={containerRef}
                isBottomMargin={isMobile}
                isLeftMargin={isMobile && ! mixedNewBuilding && offers.length === 1}
            >
                {offers.map(offer => (
                    <OfferMiniCard model={offer} key={offer.id} isOffer={isOffer} mixedNewBuilding={mixedNewBuilding} />
                ))}
            </CardScroller>
            {isMobile && onAllOffersClick && offers?.length > 4 && (
                <Button variant='secondary' fullWidth onClick={onAllOffersClick}>
                    Показать все предложения
                </Button>
            )}
        </section>
    );
}

export const SimilarNewBuildingOffersSection = ({
    isMobile,
    isOffer = false,
    onSwitchToNb,
    similarNbCollection,
    newBuildingUrl
}: {
    isMobile?: boolean;
    isOffer?: boolean;
    onSwitchToNb?: () => void;
    similarNbCollection: readonly OfferSnippetModel[];
    newBuildingUrl?: string;
}) => {
    return (<SimilarOffers
        title={isOffer ? 'Квартиры в новостройках' : 'Квартиры в новостройках.'}
        dataTestPrefix='newbuilding'
        isMobile={isMobile}
        isOffer={isOffer}
        onAllOffersClick={onSwitchToNb}
        offers={similarNbCollection}
        mixedNewBuilding
        newBuildingUrl={newBuildingUrl}
    />);
};
